export class Input {
  enabled = false;
  stick = { x: 0, y: 0 };
  private keys = new Set<string>();
  private just = new Set<string>();
  private dragging = false;
  private pointerId: number | null = null;
  private last = { x: 0, y: 0 };
  private orbit = { x: 0, y: 0 };
  private wheel = 0;
  private joystickActive = false;
  private joyPointer: number | null = null;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly joystickRoot: HTMLElement,
    private readonly knob: HTMLElement,
  ) {
    window.addEventListener("keydown", this.onKeyDown);
    window.addEventListener("keyup", this.onKeyUp);
    canvas.addEventListener("pointerdown", this.onCanvasDown);
    window.addEventListener("pointermove", this.onPointerMove);
    window.addEventListener("pointerup", this.onPointerUp);
    window.addEventListener("pointercancel", this.onPointerUp);
    canvas.addEventListener("wheel", this.onWheel, { passive: false });
    canvas.addEventListener("contextmenu", (event) => event.preventDefault());

    const base = joystickRoot.querySelector(".joystick-base") ?? joystickRoot;
    base.addEventListener("pointerdown", this.onJoyDown as EventListener);
  }

  dispose(): void {
    window.removeEventListener("keydown", this.onKeyDown);
    window.removeEventListener("keyup", this.onKeyUp);
    this.canvas.removeEventListener("pointerdown", this.onCanvasDown);
    window.removeEventListener("pointermove", this.onPointerMove);
    window.removeEventListener("pointerup", this.onPointerUp);
    window.removeEventListener("pointercancel", this.onPointerUp);
    this.canvas.removeEventListener("wheel", this.onWheel);
  }

  endFrame(): void {
    this.just.clear();
    this.orbit.x = 0;
    this.orbit.y = 0;
    this.wheel = 0;
  }

  consumeOrbit(): { x: number; y: number } {
    return { x: this.orbit.x, y: this.orbit.y };
  }

  consumeWheel(): number {
    return this.wheel;
  }

  wasPressed(code: string): boolean {
    return this.just.has(code);
  }

  get move(): { x: number; y: number } {
    if (!this.enabled) {
      return { x: 0, y: 0 };
    }
    let x = this.stick.x;
    let y = -this.stick.y;
    if (this.keys.has("KeyD") || this.keys.has("ArrowRight")) {
      x += 1;
    }
    if (this.keys.has("KeyA") || this.keys.has("ArrowLeft")) {
      x -= 1;
    }
    if (this.keys.has("KeyW") || this.keys.has("ArrowUp")) {
      y += 1;
    }
    if (this.keys.has("KeyS") || this.keys.has("ArrowDown")) {
      y -= 1;
    }
    const len = Math.hypot(x, y);
    if (len > 1) {
      x /= len;
      y /= len;
    }
    return { x, y };
  }

  showJoystick(show: boolean): void {
    this.joystickRoot.hidden = !show;
  }

  private onKeyDown = (event: KeyboardEvent): void => {
    if (event.repeat) {
      return;
    }
    if (event.code === "ArrowUp" || event.code === "ArrowDown" || event.code === "ArrowLeft" || event.code === "ArrowRight") {
      event.preventDefault();
    }
    this.keys.add(event.code);
    this.just.add(event.code);
  };

  private onKeyUp = (event: KeyboardEvent): void => {
    this.keys.delete(event.code);
  };

  private onCanvasDown = (event: PointerEvent): void => {
    if (event.button !== 0 && event.button !== 2) {
      return;
    }
    this.dragging = true;
    this.pointerId = event.pointerId;
    this.last.x = event.clientX;
    this.last.y = event.clientY;
    this.canvas.setPointerCapture(event.pointerId);
  };

  private onJoyDown = (event: PointerEvent): void => {
    event.preventDefault();
    event.stopPropagation();
    this.joystickActive = true;
    this.joyPointer = event.pointerId;
    this.updateStick(event.clientX, event.clientY);
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
  };

  private onPointerMove = (event: PointerEvent): void => {
    if (this.joystickActive && event.pointerId === this.joyPointer) {
      this.updateStick(event.clientX, event.clientY);
      return;
    }
    if (!this.dragging || event.pointerId !== this.pointerId) {
      return;
    }
    this.orbit.x += event.clientX - this.last.x;
    this.orbit.y += event.clientY - this.last.y;
    this.last.x = event.clientX;
    this.last.y = event.clientY;
  };

  private onPointerUp = (event: PointerEvent): void => {
    if (event.pointerId === this.joyPointer) {
      this.joystickActive = false;
      this.joyPointer = null;
      this.stick.x = 0;
      this.stick.y = 0;
      this.knob.style.transform = "translate(-50%, -50%)";
    }
    if (event.pointerId === this.pointerId) {
      this.dragging = false;
      this.pointerId = null;
    }
  };

  private onWheel = (event: WheelEvent): void => {
    event.preventDefault();
    this.wheel += event.deltaY;
  };

  private updateStick(clientX: number, clientY: number): void {
    const base = this.joystickRoot.querySelector(".joystick-base");
    if (!(base instanceof HTMLElement)) {
      return;
    }
    const rect = base.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const radius = rect.width / 2 - 8;
    let dx = clientX - cx;
    let dy = clientY - cy;
    const len = Math.hypot(dx, dy);
    if (len > radius) {
      dx = (dx / len) * radius;
      dy = (dy / len) * radius;
    }
    this.stick.x = dx / radius;
    this.stick.y = dy / radius;
    this.knob.style.transform = `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px))`;
  }
}

export function preferVirtualJoystick(): boolean {
  return window.matchMedia("(pointer: coarse)").matches || window.matchMedia("(hover: none)").matches || window.innerWidth < 820;
}
